import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { observer } from 'mobx-react'
import { Modal } from 'components/common/modal'
import { PositionApplicationStore } from 'stores/position-application'
import { theme } from 'infra/theme'
import { Column } from 'components/common/layout'
import {
  PositionApplicationMainBtn,
  PositionApplicationNoticeText,
  PositionApplicationSubBtn,
  PositionApplicationSubmitBtn,
} from 'containers/positions/application/styles'
import { CheckBox, CheckBoxSize } from 'components/common/check-box'
import { ProfilePreview } from 'components/scouts/profile/profile-preview'
import {
  MILLISECONDS_PER_MONTH,
  SKIP_POSITION_APPLICATION_PREVIEW_KEY,
} from 'infra/constants'
import { useIsDesktop } from 'utils/hooks'
import { useStores } from 'stores/globals'
import {
  usePositionDetail,
  useScoutMyRequest,
  useTempApplication,
  useUser,
} from 'apis/hooks'
import {
  ApplicationFulfillment,
  ApplicationFulfillmentCondition,
} from 'types/common'
import { DescText } from 'components/common/text'
import { ApplicationInfoWithoutProfile } from 'containers/positions/application/application-info-without-profile'
import { hasFitApplicationFulfillment } from 'utils/validations'
import { getProfileCompleteStatus } from 'utils/formatters'

const clearDescriptionIfNotFit = (
  items: ApplicationFulfillmentCondition[],
): ApplicationFulfillmentCondition[] => {
  return items.map((item) => ({
    ...item,
    description: item.isFit ? item.description : '',
  }))
}

const getPreviewApplicationFulfillment = (
  fulfillment: ApplicationFulfillment,
): ApplicationFulfillment => {
  return {
    task: clearDescriptionIfNotFit(fulfillment.task),
    preferred: clearDescriptionIfNotFit(fulfillment.preferred),
    qualification: clearDescriptionIfNotFit(fulfillment.qualification),
  }
}

export const PositionApplicationPreviewModal: React.FC<{
  store: PositionApplicationStore
  onClose: () => void
}> = observer(({ store, onClose }) => {
  const { positionListStore } = useStores()
  const isDesktop = useIsDesktop()
  const [isChecked, setIsChecked] = useState(false)
  const { data: user } = useUser()
  const { data: profile } = useScoutMyRequest()
  const { data: position } = usePositionDetail(store.positionDetail?.id)
  const { data: tempApplication } = useTempApplication(
    store.positionDetail?.id || 0,
  )
  // useMemo로 fulfillments 계산 최적화
  const previewFulfillments = useMemo(() => {
    return getPreviewApplicationFulfillment(store.fulfillments)
  }, [store.fulfillments])
  const {
    isCompletedCareer,
    isCompletedEducation,
    isCompletedExpectation,
    isCompletedIntroduction,
    isCompletedJobSearchingReason,
    isCompletedLanguage,
  } = getProfileCompleteStatus(profile)
  const isValidProfile = // 프로필이 "존재"해도, 보여줄 정보가 있어야 유효함
    profile &&
    (isCompletedCareer ||
      isCompletedEducation ||
      isCompletedExpectation ||
      isCompletedIntroduction ||
      isCompletedJobSearchingReason ||
      isCompletedLanguage)
  const isApplicationMsgEmpty =
    !store.joinReason &&
    !store.fitReason &&
    !hasFitApplicationFulfillment(previewFulfillments)
  // 유효한 프로필이 없고 아무런 지원 메시지를 쓰지 않았을 때
  // => 채용공고 적합도와 지원메시지를 빈 UI지만 보여주고, 하단에서 지원메시지 작성을 유도하는 문구와 CTA 버튼 배치
  const isApplicationInfoInsufficient = !isValidProfile && isApplicationMsgEmpty

  const submit = async () => {
    try {
      if (!confirm(store.applicationAlert)) return
      if (isChecked)
        localStorage.setItem(
          SKIP_POSITION_APPLICATION_PREVIEW_KEY,
          JSON.stringify({
            expire: Date.now() + MILLISECONDS_PER_MONTH,
          }),
        )
      await store.apply(!!position?.userApplication.isPossibleApply)
      positionListStore.mutatePositionList()
      store.setState('isOpenPreviewModal', false)
      store.setState('isOpenDoneModal', true)
    } catch (e) {
      alert(e)
    }
  }

  return (
    <Modal width={700} onClose={onClose}>
      <Header>
        <Column style={{ gap: 8 }}>
          <TitleText
            style={{
              fontSize: isDesktop ? theme.fontSize.h4 : theme.fontSize.desc,
            }}
          >
            <span>{store.startupDetail?.name}</span>에게 이렇게 보여요
          </TitleText>
          <TitleText
            style={{
              fontSize: isDesktop
                ? theme.fontSize.default
                : theme.fontSize.small,
              lineHeight: isDesktop ? '24px' : '20px',
            }}
          >
            프로필은 지원 후에도 수정 가능하며
            {isDesktop ? ' ' : <br />}
            지원서에 즉시 반영돼요
            <br />(<span>마이페이지 {'>'} 내 프로필</span>
            에서 수정)
          </TitleText>
        </Column>
        <CheckBox
          size={CheckBoxSize.SMALL}
          checked={isChecked}
          onClick={() => setIsChecked((prev) => !prev)}
        >
          당분간 보지 않기
        </CheckBox>
      </Header>
      {isValidProfile ? (
        <ProfilePreview
          application={{
            files: store.files,
            links: tempApplication?.links,
            joinReason: store.joinReason,
            fitReason: store.fitReason,
            fulfillments: previewFulfillments,
          }}
        />
      ) : (
        <Content>
          <ApplicationInfoWithoutProfile
            user={{
              requestId: null,
              image: user?.image || '',
              name: user?.name || '',
              experience: user?.experience || 0,
              birthYear: user?.birthYear || null,
              positions: user?.interestedPositionTypes || [],
              workLevel: null,
              jobInterestLevel: null,
              careers: [],
              isActivelySeeker: false,
              isCommonCurated: false,
              idpCertificates: [],
            }}
            application={{
              files: store.files.map((x) => ({ ...x, isAccessible: true })),
              links:
                tempApplication?.links.map((x) => ({
                  ...x,
                  isAccessible: true,
                })) || [],
              joinReason: store.joinReason,
              fitReason: store.fitReason,
              fulfillments: previewFulfillments,
            }}
            showEmptyContent={isApplicationInfoInsufficient}
          />
        </Content>
      )}
      <Bottom>
        {isApplicationMsgEmpty && (
          <PositionApplicationNoticeText>
            성의 있는 메시지로 서류 합격 가능성을 최대 <span>6배</span>{' '}
            높여보세요!
          </PositionApplicationNoticeText>
        )}
        <BtnContainer>
          {isApplicationInfoInsufficient ? (
            <>
              <PositionApplicationSubBtn onClick={submit}>
                이대로 지원
              </PositionApplicationSubBtn>
              <PositionApplicationMainBtn
                onClick={() => {
                  store.setState('isOpenPreviewModal', false)
                  store.setState('curStep', 1) // isApplicationInfoInsufficient, 즉 아무 지원메시지 없는 상태이므로 "합류하고 싶은 이유"로 보냄
                }}
              >
                지원 메시지 작성하기
              </PositionApplicationMainBtn>
            </>
          ) : (
            <>
              <PositionApplicationSubBtn
                onClick={() => store.setState('isOpenPreviewModal', false)}
              >
                이전
              </PositionApplicationSubBtn>
              <PositionApplicationSubmitBtn onClick={submit}>
                지원하기
              </PositionApplicationSubmitBtn>
            </>
          )}
        </BtnContainer>
      </Bottom>
    </Modal>
  )
})

const Header = styled(Column)`
  gap: 16px;
  padding: 40px 0;
  text-align: center;
  align-items: center;
  background-color: ${(p) => p.theme.color.background3};
`

const TitleText = styled(DescText)`
  span {
    font-weight: ${(p) => p.theme.fontWeight.bold};
  }
`

const Content = styled(Column)`
  padding: 32px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 32px 16px;
  }
`

const Bottom = styled(Column)`
  position: sticky;
  bottom: 0;
  padding: 16px;
  gap: 8px;
  background-color: ${(p) => p.theme.color.white1};
  @media ${(p) => p.theme.deviceSize.desktop} {
    padding: 24px 40px;
  }
`

const BtnContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 8px;
`
